import React, { useContext, useEffect, useState } from 'react';
import { MailContext } from './Context';
import type { AppMailReplyProps, } from '../../Models/Template/demo';
import { Message } from '@microsoft/microsoft-graph-types';
import { Chips, ChipsAddEvent, ChipsRemoveEvent } from 'primereact/chips';
import { Button, Editor, InputText, Tooltip } from '../../Common/Imports';
import DOMPurify from 'dompurify';

function AppMailReply(props: AppMailReplyProps) {
    const content = props.content;
    const { onSend, MailBox } = useContext(MailContext);
    const [To, setTo] = useState<string[]>([]);
    const [CC, setCC] = useState<string[]>([]);
    const [newMail, setNewMail] = useState<Message>({
        id: "",
        from: { emailAddress: { address: MailBox } },
        toRecipients: content?.from ? [content?.from] : undefined,
        subject: content?.subject ?? '',
        body: { content: '', contentType: 'html' }
    });
    const [displayMessage, setDisplayMessage] = useState(false);

    useEffect(() => {
        switch (props.openType) {
            case 0:
                if (content?.from?.emailAddress?.address) {
                    setTo([content?.from?.emailAddress?.address])
                }
                break;
            case 1:
                if (content?.from?.emailAddress?.address) {
                    setTo([content?.from?.emailAddress?.address])
                }
                if (content?.ccRecipients) {
                    const _T: string[] = content?.ccRecipients
                        .map(m => m.emailAddress?.address) // Map to extract email addresses
                        .filter((address): address is string => !!address) // Ensure it's typed as a string
                        ?? [];
                    setCC(_T);
                }
                break;
        }

    }, [content?.ccRecipients, content?.from?.emailAddress?.address, props.openType]);

    const sendMail = async () => {
        const mailToSend: Message = {
            ...newMail,
            toRecipients: To.map(m => ({ emailAddress: { address: m } })),
            ccRecipients: CC.map(m => ({ emailAddress: { address: m } })),
            subject: newMail.subject ?? content?.subject ?? '',
            body: {
                ...newMail.body,
                content: newMail.body?.content ?? '',
                contentType: newMail.body?.contentType ?? 'html'
            }
        };

        const sent = await onSend(mailToSend);
        if (sent) {
            props.hide();
        }
    };

    const toggleMessage = () => {
        setDisplayMessage((prevState) => !prevState);
    };

    const AddTo = (e: ChipsAddEvent) => {
        if (To && !To.includes(e.value)) {
            const _T = [...To, e.value];
            setTo(_T);
        } else {
            return false;
        }
    };

    const AddCC = (e: ChipsAddEvent) => {
        if (CC && !CC.includes(e.value)) {
            const _T = [...CC, e.value];
            setCC(_T);
        } else {
            return false;
        }
    }

    const RemoveTo = (e: ChipsRemoveEvent) => {
        if (To) {
            const _T = To.filter(item => item !== e.value);
            setTo(_T);
        }
    }

    const RemoveCC = (e: ChipsRemoveEvent) => {
        if (CC) {
            const _T = CC.filter(item => item !== e.value);
            setCC(_T);
        }
    }

    return (
        <React.Fragment>
            {content ? (
                <>
                    <div className="row">
                        <div className="col-md-12 p-fluid">
                            <label htmlFor="username">To</label>
                            <Chips value={To} onAdd={AddTo} onRemove={RemoveTo} className="w-100" separator=";" />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12  p-fluid">
                            <label htmlFor="username">CC</label>
                            <Chips value={CC} onAdd={AddCC} onRemove={RemoveCC} className="w-100" separator=";" />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <label htmlFor="Subject" className="block text-900 font-semibold mb-3">
                                Subject
                            </label>
                            <span className="p-input-icon-left w-full" style={{ height: '3.5rem' }}>
                                <i className="pi pi-pencil" style={{ left: '1.5rem' }}></i>
                                <InputText
                                    id="subject"
                                    type="text"
                                    value={newMail.subject}
                                    onChange={(e) =>
                                        setNewMail((prevState) => ({
                                            ...prevState,
                                            subject: e.target.value
                                        }))
                                    }
                                    placeholder="Subject"
                                    className="w-full pl-7 text-900 font-semibold"
                                    style={{ height: '3.5rem' }}
                                />
                            </span>
                        </div>
                    </div>
                    {displayMessage ? (
                        <div
                            className="border-1 surface-border border-round p-4"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(content.body?.content ?? '', {
                                    USE_PROFILES: { html: true },
                                    FORBID_TAGS: ['form', 'iframe', 'object', 'embed', 'svg', 'math']
                                })
                            }}
                        />
                    ) : null}
                    <div className="col-12 field">
                        <Tooltip target=".toggle-content" content={displayMessage ? 'Hide Content' : 'Show Content'} />
                        <span className="toggle-content surface-ground cursor-pointer border-round px-2" onClick={toggleMessage}>
                            <i className="pi pi-ellipsis-h"></i>
                        </span>
                        <Editor
                            style={{ height: '250px' }}
                            className="mt-3"
                            value={newMail.body?.content ?? ""}
                            onTextChange={(e) =>
                                setNewMail((prevState) => ({
                                    ...prevState,
                                    body: { ...prevState.body, content: e.htmlValue ?? '' }
                                }))
                            }
                        ></Editor>
                    </div>
                    <div className="flex column-gap-3 justify-content-end p-5 border-top-1 surface-border">
                        <Button type="button" className="h-3rem" icon="pi pi-send" label="Send" onClick={sendMail}></Button>
                    </div>
                </>
            ) : null
            }
        </React.Fragment >
    );
}

export default AppMailReply;
